<div class="bv-error-container">
  <div class="bv-error-content">
    <h1 #bvErrorTitle class="iris-h1-responsive">{{ config.title }}</h1>
    <p class="iris-body-responsive">{{ formatSubtitle() }}</p>
    
    @switch (data?.errorType) {
      @case (BvErrorTypes.SUGGESTED_PAYER) {
        <p class="iris-body-responsive if-correct-text">
          If this looks correct, we'll check your coverage under this insurance.
        </p>
        
        @if (showMemberDetails) {
          <div class="what-we-found-section">
            <h4 class="iris-h4-responsive what-we-found-title">What we found</h4>
            
            <div class="suggested-insurance-container">
              <div class="insurance-label">Insurance</div>
              <div class="insurance-name-highlight">{{ suggestedInsuranceName }}</div>
              
              <div class="member-details-grid">
                <div class="member-detail-item">
                  <div class="iris-body-responsive">Member name</div>
                  <div class="iris-body-bold">{{ data?.memberName || '-' }}</div>
                </div>
                
                <div class="member-detail-item">
                  <div class="iris-body-responsive">Date of birth</div>
                  <div class="iris-body-bold">{{ data?.dateOfBirth || '-' }}</div>
                </div>
                
                <div class="member-detail-item">
                  <div class="iris-body-responsive">Member ID</div>
                  <div class="iris-body-bold">{{ data?.memberId || '-' }}</div>
                </div>
                
                <div class="member-detail-item">
                  <div class="iris-body-responsive">Group number</div>
                  <div class="iris-body-bold">{{ data?.groupNumber || '-' }}</div>
                </div>
              </div>
            </div>
          </div>
        }
      }
      @case (BvErrorTypes.UNKNOWN_COSTSHARE) {
        <p class="iris-body-responsive"><br>
          We're not yet able to provide a cost estimate for your plan but you can call your insurance company using the member services number on the back of your card to get details about the cost of your session.
        </p>
        
        <div class="unknown-costshare-icon-container">
          <img src="/assets/icons/unknown_cost_share.svg" alt="Insurance verification successful" class="unknown-costshare-icon" />
        </div>
      }
      @case (BvErrorTypes.MEDICAID_PLAN) {
        <p class="iris-body-bold-responsive medicaid-plan-next-steps-title">Next steps we recommend:</p>
        
        <ul class="medicaid-plan-list">
          <li class="iris-body-responsive">
            Contact your Medicaid plan for a list of in-network providers who can meet your healthcare needs.
          </li>
        </ul>
      }
      @case (BvErrorTypes.COVERAGE_NOT_STARTED) {
        <p class="iris-body-bold-responsive what-you-can-do-title">What you can do:</p>
        
        <ul class="coverage-options-list">
          <li class="iris-body-responsive">
            Add a different insurance with active coverage that you can use instead
          </li>
          <li class="iris-body-responsive">
            Continue with self-pay rates for sessions
          </li>
          <li class="iris-body-responsive">
            Add this insurance to your account and use it for sessions <strong>after your plan start date</strong>
          </li>
        </ul>
      }
      @case (BvErrorTypes.COVERAGE_EXPIRED) {
        <p class="iris-body-responsive">
          If you have a current insurance plan, you can go back and enter it or you can continue with our self-pay rate for sessions.
        </p>
      }
      @case (BvErrorTypes.NO_COVERAGE) {
        <p class="iris-body-bold-responsive what-you-can-do-title">What you can do:</p>

        <ul class="coverage-options-list">
          <li class="iris-body-responsive">
            Call your insurance company using the number on the back of your card to verify if you have mental health coverage or if it's through a third-party
          </li>
          <li class="iris-body-responsive">
            Try a different insurance plan if you have other coverage available
          </li>
          <li class="iris-body-responsive">
            Continue with self-pay rates for sessions
          </li>
        </ul>
      }
      @case (BvErrorTypes.INSURANCE_OUT_OF_NETWORK) {
        <!-- Content handled by self-pay estimate display -->
      }
      @default {
        <p class="iris-body-responsive if-correct-text">
          If this looks correct, we'll check your coverage under this insurance.
        </p>
      }
    }

    @if (showSelfPayEstimateDetails()) {
      <div class="bv-error-self-pay-estimate">
        <client-self-pay-cost-estimate-detailed [standAlone]="true"></client-self-pay-cost-estimate-detailed>
      </div>
    }
  </div>

  <div class="bv-error-actions">
    @switch (data?.errorType) {
      <!-- PAYOR_DOWNTIME: Single button layout -->
      @case (BvErrorTypes.PAYOR_DOWNTIME) {
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.PRIMARY"
            data-test="bv-error-primary-action"
            (click)="onPrimaryAction()"
          >
            {{ config.primaryButtonText }}
          </iris-button>
        </iris-button-container>
      }
      <!-- COVERAGE_NOT_STARTED: Three button layout -->
      @case (BvErrorTypes.COVERAGE_NOT_STARTED) {
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.PRIMARY"
            data-test="bv-error-primary-action"
            (click)="onPrimaryAction()"
          >
            {{ config.primaryButtonText }}
          </iris-button>
        </iris-button-container>
        
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.SECONDARY"
            data-test="bv-error-secondary-action"
            (click)="onSecondaryAction()"
          >
            {{ config.secondaryLinkText }}
          </iris-button>
        </iris-button-container>
        
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.SECONDARY"
            data-test="bv-error-tertiary-action"
            (click)="onTertiaryAction()"
          >
            {{ config.tertiaryLinkText }}
          </iris-button>
        </iris-button-container>
      }
      <!-- COVERAGE_EXPIRED: Three button layout -->
      @case (BvErrorTypes.COVERAGE_EXPIRED) {
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.PRIMARY"
            data-test="bv-error-primary-action"
            (click)="onPrimaryAction()"
          >
            {{ config.primaryButtonText }}
          </iris-button>
        </iris-button-container>
      
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.SECONDARY"
            data-test="bv-error-secondary-action"
            (click)="onSecondaryAction()"
          >
            {{ config.secondaryLinkText }}
          </iris-button>
        </iris-button-container>
    
        <div class="tertiary-action-container">
          <iris-icon 
            class="info-icon" 
            name="info"
            colorToken="$color-neutral-60"
            [size]="IconSizes.SM">
          </iris-icon>
          <span class="tertiary-action-text">
            You can still
            <a 
              href="#" 
              class="tertiary-link" 
              data-test="bv-error-tertiary-action"
              (click)="onTertiaryAction(); $event.preventDefault()"
            >
              {{ config.tertiaryLinkText }}
            </a>
            but your sessions may not be covered. Our billing team will attempt to manually verify your insurance and reach out if they find out more information.
          </span>
        </div>
      }
      <!-- NO_COVERAGE: Three button layout -->
      @case (BvErrorTypes.NO_COVERAGE) {
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.PRIMARY"
            data-test="bv-error-primary-action"
            (click)="onPrimaryAction()"
          >
            {{ config.primaryButtonText }}
          </iris-button>
        </iris-button-container>
        
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.SECONDARY"
            data-test="bv-error-secondary-action"
            (click)="onSecondaryAction()"
          >
            {{ config.secondaryLinkText }}
          </iris-button>
        </iris-button-container>
        
        <div class="tertiary-action-container">
          <iris-icon 
            class="info-icon" 
            name="info"
            colorToken="$color-neutral-60"
            [size]="IconSizes.SM">
          </iris-icon>
          <span class="tertiary-action-text">
            You can still
            <a 
              href="#" 
              class="tertiary-link" 
              data-test="bv-error-tertiary-action"
              (click)="onTertiaryAction(); $event.preventDefault()"
            >
              {{ config.tertiaryLinkText }}
            </a>
            but your sessions may not be covered. Our billing team will attempt to manually verify your insurance and reach out if they find out more information.
          </span>
        </div>
      }
      <!-- SUGGESTED_PAYER: Full width button layout -->
      @case (BvErrorTypes.SUGGESTED_PAYER) {
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          @if (config.showPrimaryAction !== false) {
            <iris-button
              [type]="IrisButtonTypes.PRIMARY"
              data-test="bv-error-primary-action"
              (click)="onPrimaryAction()"
            >
              {{ config.primaryButtonText }}
            </iris-button>
          }
        </iris-button-container>
        
        @if (config.showSecondaryAction !== false && config.secondaryLinkText) {
          <div class="secondary-action-container">
            <iris-icon 
              class="info-icon" 
              name="info"
              colorToken="$color-neutral-60"
              [size]="IconSizes.SM">
            </iris-icon>
            <span class="secondary-action-text">
              If this is not correct, you can
              <a 
                href="#" 
                class="secondary-link" 
                data-test="bv-error-secondary-action"
                (click)="onSecondaryAction(); $event.preventDefault()"
              >
                {{config.secondaryLinkText}}
              </a>
              but your sessions may not be covered. Our billing team will attempt to manually verify your insurance and reach out if they find out more information.
            </span>
          </div>
        }
      }
      <!-- MEDICAID_PLAN: Single button layout -->
      @case (BvErrorTypes.MEDICAID_PLAN) {
        <iris-button-container 
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          @if (config.showPrimaryAction !== false) {
            <iris-button
              [type]="IrisButtonTypes.PRIMARY"
              data-test="bv-error-primary-action"
              (click)="onPrimaryAction()"
            >
              {{ config.primaryButtonText }}
            </iris-button>
          }
        </iris-button-container>
      }
      <!-- INSURANCE_OUT_OF_NETWORK: Two button layout -->
      @case (BvErrorTypes.INSURANCE_OUT_OF_NETWORK) {
        <iris-button-container
          class="primary-button-with-margin"
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.PRIMARY"
            data-test="bv-error-primary-action"
            (click)="onPrimaryAction()"
          >
            {{ config.primaryButtonText }}
          </iris-button>
        </iris-button-container>

        <iris-button-container
          [mobileLayout]="IrisButtonContainerMobileLayouts.FULLWIDTH"
          [desktopLayout]="IrisButtonContainerDesktopLayouts.FULLWIDTH">
          <iris-button
            [type]="IrisButtonTypes.SECONDARY"
            data-test="bv-error-secondary-action"
            (click)="onSecondaryAction()"
          >
            {{ config.secondaryLinkText }}
          </iris-button>
        </iris-button-container>
      }
      <!-- Default layout for other error types -->
      @default {
        <iris-button-container [mobileLayout]="isMobile ? IrisButtonContainerMobileLayouts.FULLWIDTH : IrisButtonContainerMobileLayouts.SIDEBYSIDE">
          @if (config.showPrimaryAction !== false) {
            <iris-button
              [type]="IrisButtonTypes.PRIMARY"
              data-test="bv-error-primary-action"
              (click)="onPrimaryAction()"
            >
              {{ config.primaryButtonText }}
            </iris-button>
          }
        </iris-button-container>
        
        @if (config.showSecondaryAction !== false && config.secondaryLinkText) {
          <div class="secondary-action-container">
            <iris-icon 
              class="info-icon" 
              name="info"
              colorToken="$color-neutral-60"
              [size]="IconSizes.SM">
            </iris-icon>
            <span class="secondary-action-text">
              @switch (data?.errorType) {
                @case (BvErrorTypes.SUGGESTED_PAYER) {
                  If this is not correct, you can
                  <a 
                    href="#" 
                    class="secondary-link" 
                    data-test="bv-error-secondary-action"
                    (click)="onSecondaryAction(); $event.preventDefault()"
                  >
                    {{config.secondaryLinkText}}
                  </a>
                  but your sessions may not be covered. Our billing team will attempt to manually verify your insurance and reach out if they find out more information.
                }
                @default {
                  If this is not correct, you can
                  <a 
                    href="#" 
                    class="secondary-link" 
                    data-test="bv-error-secondary-action"
                    (click)="onSecondaryAction(); $event.preventDefault()"
                  >
                    {{config.secondaryLinkText}}
                  </a>
                  but your sessions may not be covered. Our billing team will attempt to manually verify your insurance and reach out if they find out more information.
                }
              }
            </span>
          </div>
        }
    
        @if (config.showTertiaryAction && config.tertiaryLinkText) {
          <div class="tertiary-action-container">
            <iris-icon 
              class="info-icon" 
              name="info"
              colorToken="$color-neutral-60"
              [size]="IconSizes.SM">
            </iris-icon>
            <span class="tertiary-action-text">
              You can still
              <a 
                href="#" 
                class="tertiary-link" 
                data-test="bv-error-tertiary-action"
                (click)="onTertiaryAction(); $event.preventDefault()"
              >
                {{config.tertiaryLinkText}}
              </a>
              but your sessions may not be covered. Our billing team will attempt to manually verify your insurance and reach out if they find out more information.
            </span>
          </div>
        }
      }
    }
  </div>
</div> 